if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      if (!this.productInfo?.id.startsWith('MainProduct-') || this.closest('quick-add-modal')) return;
      this.mainForm = this.productInfo.querySelector('product-form');
      this.mainButton = this.mainForm?.querySelector('[type="submit"]');
      if (!this.mainButton) return;

      this.button = this.querySelector('[data-sticky-submit]');
      this.onChange = this.handleChange.bind(this);
      this.onClick = () => {
        if (this.button.disabled) return;
        this.submitPending = true;
        this.syncButton();
        // Allow the loading state to paint before third-party submit/fetch hooks run.
        this.submitFrame = requestAnimationFrame(() => {
          this.submitFrame = requestAnimationFrame(() => {
            this.submitFrame = null;
            if (this.isConnected) this.submitStickyAtc();
          });
        });
      };
      this.addEventListener('change', this.onChange);
      this.button.addEventListener('click', this.onClick);
      this.variant = JSON.parse(this.querySelector('[data-sticky-variant]').textContent);
      this.lastUrlVariant = new URL(window.location.href).searchParams.get('variant');
      this.onUrlChange = () => {
        const variantId = new URL(window.location.href).searchParams.get('variant');
        if (variantId === this.lastUrlVariant) return;
        this.lastUrlVariant = variantId;
        const url = new URL(this.productInfo.dataset.url, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        this.loadSelection(url);
      };
      window.addEventListener('popstate', this.onUrlChange);
      if (!window.__stickyAtcHistoryPatched) {
        ['pushState', 'replaceState'].forEach((method) => {
          const original = history[method];
          history[method] = function (...args) {
            const result = original.apply(this, args);
            window.dispatchEvent(new Event('sticky-atc:urlchange'));
            return result;
          };
        });
        window.__stickyAtcHistoryPatched = true;
      }
      window.addEventListener('sticky-atc:urlchange', this.onUrlChange);
      this.spacer = document.createElement('div');
      this.spacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.spacer);
      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries.find(({ target }) => target === this);
        if (!entry) return;
        const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
        // Reuse the delivered layout measurement; retain a legacy fallback.
        const height = this.hidden ? 0 : box?.blockSize ?? this.offsetHeight;
        const value = `${Math.round(height)}px`;
        if (this.spacer.style.height !== value) this.spacer.style.height = value;
      });
      this.resizeObserver.observe(this, { box: 'border-box' });
      this.scheduleVisibility = () => {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.updateVisibility();
        });
      };
      // Include dynamically injected bundle selectors, not only the submit button.
      this.visibilityTargetsDirty = true;
      this.purchaseObserver = new MutationObserver((records) => {
        const purchaseRecords = records.filter(({ target }) => !this.contains(target));
        if (!purchaseRecords.length) return;
        if (purchaseRecords.some(({ type, attributeName }) =>
          type === 'childList' || ['id', 'for', 'type', 'role', 'form'].includes(attributeName)
        )) this.visibilityTargetsDirty = true;
        this.scheduleVisibility();
      });
      this.purchaseObserver.observe(this.productInfo, { subtree: true, childList: true, attributes: true });
      this.purchaseResizeObserver = new ResizeObserver(this.scheduleVisibility);
      this.purchaseResizeObserver.observe(this.productInfo);
      window.addEventListener('scroll', this.scheduleVisibility, { passive: true });
      window.addEventListener('resize', this.scheduleVisibility);
      this.scheduleVisibility();
      this.syncButton();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.submitFrame);
      this.submitPending = false;
      window.removeEventListener('popstate', this.onUrlChange);
      window.removeEventListener('sticky-atc:urlchange', this.onUrlChange);
      this.selectionController?.abort();
      this.purchaseObserver?.disconnect();
      this.purchaseResizeObserver?.disconnect();
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null;
      window.removeEventListener('scroll', this.scheduleVisibility);
      window.removeEventListener('resize', this.scheduleVisibility);
      this.resizeObserver?.disconnect();
      this.spacer?.remove();
      this.removeEventListener('change', this.onChange);
      this.button?.removeEventListener('click', this.onClick);
    }

    updateVisibility() {
      if (this.visibilityTargetsDirty) {
        const targets = [this.mainForm, this.mainButton,
          ...this.productInfo.querySelectorAll('variant-selects, select, input[type="radio"], [role="radiogroup"]')];
        // Cache membership, never positions: scroll and plugin styles still need fresh geometry.
        this.visibilityTargets = [...new Set(targets.flatMap((target) => {
          if (this.contains(target)) return [];
          return target.matches('input[type="radio"]')
            ? [target, ...(target.labels || [])] : [target];
        }))];
        this.visibilityTargetsDirty = false;
      }
      const rects = this.visibilityTargets.flatMap((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return [];
        const style = getComputedStyle(element);
        return style.visibility !== 'hidden' && style.display !== 'none' ? [rect] : [];
      });
      const top = Math.min(...rects.map((rect) => rect.top));
      const bottom = Math.max(...rects.map((rect) => rect.bottom));
      const purchaseVisible = top < window.innerHeight && bottom > 0;
      if (this.hidden !== purchaseVisible) this.hidden = purchaseVisible;
    }

    async fetchSingleItem(url, config) {
      const iframe = document.createElement('iframe');
      iframe.hidden = true;
      iframe.title = 'Cart request';
      document.body.append(iframe);
      try {
        const nativeFetch = iframe.contentWindow.fetch.bind(iframe.contentWindow);
        const response = await nativeFetch(new URL(url, window.location.href).href, config);
        // Keep the frame alive until the response body has been consumed.
        return { ok: response.ok, result: await response.json() };
      } finally {
        iframe.remove();
      }
    }

    async submitStickyAtc() {
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      this.stickyError = '';
      try {
        const variant = JSON.parse(this.querySelector('[data-sticky-variant]').textContent);
        if (!variant?.id || !variant.available) throw new Error(window.variantStrings.unavailable);
        // Build an independent payload: never inherit bundle quantities or properties.
        const body = new FormData();
        body.set('id', variant.id);
        body.set('quantity', '1');
        if (cart) {
          body.set('sections', cart.getSectionsToRender().map((section) => section.id).join(','));
          body.set('sections_url', window.location.pathname);
          cart.setActiveElement(this.button);
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        // Use an unpatched same-origin fetch so Rappi's page-level fetch wrapper
        // cannot mirror this request with its own bundle quantity.
        const { ok, result } = await this.fetchSingleItem(routes.cart_add_url, {
          ...config, body, credentials: 'same-origin',
        });
        if (!ok || result.status) {
          throw new Error(result.description || result.message || window.cartStrings.error);
        }
        // Kaching replaces the native drawer and exposes its own refresh/open API.
        // Use it when present so the isolated add is reflected in the active cart UI.
        const kachingCart = window.kachingCartApi;
        if (kachingCart?.refreshCart && kachingCart?.openCart) {
          try {
            await kachingCart.refreshCart();
            await kachingCart.openCart();
          } catch (error) {
            console.error('[sticky-atc] Kaching cart refresh failed', error);
            window.location = routes.cart_url;
            return;
          }
        } else if (cart) {
          try {
            const sections = cart.getSectionsToRender();
            if (sections.some(({ id }) => typeof result.sections?.[id] !== 'string')) {
              const url = new URL(window.location.href);
              url.searchParams.set('sections', sections.map(({ id }) => id).join(','));
              const refreshed = await this.fetchSingleItem(url.href, { credentials: 'same-origin' });
              if (!refreshed.ok || sections.some(({ id }) => typeof refreshed.result?.[id] !== 'string')) {
                throw new Error('Unable to refresh cart sections');
              }
              result.sections = refreshed.result;
            }
            cart.classList.remove('is-empty');
            cart.renderContents(result);
            cart.open(this.button);
          } catch (error) {
            // The item is already added. Show the cart page instead of inviting a retry.
            console.error('[sticky-atc] Cart refresh failed', error);
            window.location = routes.cart_url;
            return;
          }
        } else {
          window.location = routes.cart_url;
          return;
        }
        try {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-atc', productVariantId: variant.id, cartData: result,
          });
        } catch (error) {
          console.error('[sticky-atc] Cart subscriber failed', error);
        }
      } catch (error) {
        this.stickyError = error.message || window.cartStrings.error;
      } finally {
        this.submitPending = false;
        if (this.isConnected) this.syncButton();
      }
    }

    syncButton() {
      const loading = Boolean(this.submitPending);
      this.button.disabled = loading || Boolean(this.selectionPending) || !this.variant?.available;
      this.button.classList.toggle('loading', loading);
      this.button.setAttribute('aria-busy', String(loading));
      this.querySelector('.loading__spinner').classList.toggle('hidden', !loading);
      const label = !this.variant ? window.variantStrings.unavailable
        : this.variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
      this.querySelector('[data-sticky-label]').textContent = label;
      this.querySelector('[data-sticky-mobile-label]').textContent = label === window.variantStrings.addToCart ? 'Add' : label;
      this.button.setAttribute('aria-haspopup', 'dialog');
      const error = this.querySelector('[data-sticky-error]');
      error.hidden = !this.stickyError;
      error.textContent = this.stickyError || '';
      this.querySelectorAll('[data-sticky-option]').forEach((select) => { select.disabled = loading; });
    }

    handleChange(event) {
      if (!event.target.matches('[data-sticky-option]')) return;
      event.stopPropagation();
      const url = new URL(event.target.selectedOptions[0].dataset.productUrl || this.productInfo.dataset.url, window.location.origin);
      url.searchParams.set('option_values', Array.from(this.querySelectorAll('[data-sticky-option]'), (option) => option.value).join(','));
      this.loadSelection(url);
    }

    async loadSelection(url) {
      this.selectionController?.abort();
      const controller = new AbortController();
      this.selectionController = controller;
      this.selectionPending = true;
      this.stickyError = '';
      this.syncButton();
      url.searchParams.set('section_id', this.dataset.section);
      try {
        const response = await fetch(url.href, { signal: controller.signal });
        if (!response.ok) throw new Error(window.cartStrings.error);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (controller.signal.aborted || !this.isConnected) return;
        this.updateFromHTML(html);
      } catch (error) {
        if (controller.signal.aborted) return;
        this.variant = null;
        this.stickyError = error.message;
      } finally {
        if (this.selectionController === controller) {
          this.selectionPending = false;
          if (this.isConnected) this.syncButton();
        }
      }
    }

    updateFromHTML(html) {
      const source = html.querySelector('sticky-atc');
      if (!source) throw new Error(window.cartStrings.error);
      // Preserve select nodes and focus; only reconcile option data.
      this.querySelectorAll('[data-sticky-option]').forEach((select, index) => {
        const next = source.querySelectorAll('[data-sticky-option]')[index];
        if (next) {
          select.innerHTML = next.innerHTML;
          select.value = next.value;
        }
      });
      ['[data-sticky-price]', '[data-sticky-image]', '[data-sticky-variant]'].forEach((selector) => {
        this.querySelector(selector).innerHTML = source.querySelector(selector).innerHTML;
      });
      this.variant = JSON.parse(source.querySelector('[data-sticky-variant]').textContent);
      this.querySelector('[data-sticky-price]').hidden = !this.variant;
    }
  });
}
